import React, { useCallback, useEffect, useMemo, useState } from "react";
import { webAlert, webConfirm } from "../webDialog";
import { Icon } from "../components/Icon";
import { WebMascot } from "../components/WebMascot";
import { WebVideoCard, WebVideo } from "../components/WebVideoCard";
import { useAuthStore } from "../../state/authStore";
import { Playlist, PlaylistItemVideo, playlistsApi } from "../../api/playlists";
import { prettyTitle } from "../prettyTitle";
import { videoMinutes } from "../formatDuration";
import {
  adminContentApi,
  contentApi,
  GeneratedVideo,
  VideoListItem,
} from "../../api/content";

// Map a playlist's narrow video shape onto the WebVideoCard model so the
// playlist detail view renders the same cards as Home.
const toWebVideo = (v: PlaylistItemVideo): WebVideo => ({
  id: v.id,
  title: prettyTitle(v.title) || v.title,
  category: v.categories?.[0] ?? "Video",
  duration: v.duration,
  isPremium: v.isPremium,
  ageMin: v.ageMin,
  ageMax: v.ageMax,
  thumbUrl: v.thumbUrl ?? null,
});

// ─────────────────────────────────────────────────────────────────────────
// WebPlaylists — parent-only management UI for curated video queues.
//
// Top-level: a grid of playlist cards (colored thumb + name + count/min).
// Clicking a card opens the editor modal (rename, recolor, add/remove
// videos, delete). The "+ New playlist" CTA creates an empty playlist and
// drops the parent straight into the editor.
//
// Family scope only. Kids never see playlists yet (TV-side surface is a
// follow-up). The backend enforces ownership; we still gate the UI here
// because every successful call returns the refreshed playlist.
// ─────────────────────────────────────────────────────────────────────────

// Color palette for new playlists when the parent hasn't picked one yet.
// Deterministic hash on the playlist id picks a slot so the card art is
// stable across renders without persisting a color.
const PALETTE = [
  "#3D3A75", // deep purple (matches the "Wind-down" card in the design)
  "#FF8A4C", // orange
  "#5B8DEF", // blue
  "#7AC74F", // green
  "#F18EB6", // pink
  "#8C7DD9", // lavender
];

function pickColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function totalMinutes(items: { video: { duration: number } }[]): number {
  const secs = items.reduce((acc, it) => acc + (it.video?.duration ?? 0), 0);
  return Math.max(1, Math.round(secs / 60));
}

export function WebPlaylists({ onPlay }: { onPlay: (v: WebVideo) => void }) {
  const { accessToken } = useAuthStore();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The playlist whose videos we're browsing (detail view). Distinct from
  // `openEditorId`, which opens the management modal (rename/color/delete).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openEditorId, setOpenEditorId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setLoadError(null);
    try {
      const list = await playlistsApi.list(accessToken);
      setPlaylists(list);
    } catch (e) {
      setLoadError(
        e instanceof Error ? e.message : "Couldn't load playlists.",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleNew = useCallback(async () => {
    if (!accessToken || creating) return;
    setCreating(true);
    try {
      const created = await playlistsApi.create(
        { name: "New playlist" },
        accessToken,
      );
      setPlaylists((prev) => [created, ...prev]);
      // Drop the parent straight into the editor — they'll almost
      // certainly want to rename + add videos right away.
      setOpenEditorId(created.id);
    } catch (e) {
      webAlert(
        e instanceof Error ? e.message : "Couldn't create playlist.",
      );
    } finally {
      setCreating(false);
    }
  }, [accessToken, creating]);

  const handleApply = useCallback((updated: Playlist) => {
    setPlaylists((prev) =>
      prev.map((p) => (p.id === updated.id ? updated : p)),
    );
  }, []);

  const handleDelete = useCallback((id: string) => {
    setPlaylists((prev) => prev.filter((p) => p.id !== id));
    setOpenEditorId(null);
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const openPlaylist = useMemo(
    () => playlists.find((p) => p.id === openEditorId) ?? null,
    [playlists, openEditorId],
  );
  const selectedPlaylist = useMemo(
    () => playlists.find((p) => p.id === selectedId) ?? null,
    [playlists, selectedId],
  );

  // ── Detail / browse view — the playlist's videos as Home-style cards. ──
  if (selectedPlaylist) {
    const minutes = totalMinutes(selectedPlaylist.items);
    return (
      <div className="fadeup w-resppad" style={{ padding: "24px 32px 64px" }}>
        <button
          className="btn btn-ghost"
          onClick={() => setSelectedId(null)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 16,
            padding: "8px 14px",
          }}
        >
          ← All playlists
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            marginBottom: 22,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "var(--display)",
                fontSize: 34,
                fontWeight: 900,
                letterSpacing: "-0.02em",
                color: "var(--ink)",
                margin: 0,
              }}
            >
              {selectedPlaylist.name}
            </h1>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "var(--ink-3)",
                marginTop: 6,
              }}
            >
              {selectedPlaylist.items.length}{" "}
              {selectedPlaylist.items.length === 1 ? "video" : "videos"} ·{" "}
              {minutes} min
            </div>
          </div>
          <button
            className="btn btn-ghost"
            onClick={() => setOpenEditorId(selectedPlaylist.id)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Icon name="controls" size={14} /> Edit playlist
          </button>
        </div>

        {selectedPlaylist.items.length === 0 ? (
          <div className="wpl-empty-card">
            <WebMascot mood="happy" color="#FF8A4C" size={120} />
            <div className="wpl-empty-title">No videos yet</div>
            <div className="wpl-empty-sub">
              Add videos to this playlist while watching — open a video and tap
              &ldquo;Add to playlist&rdquo;. Or use{" "}
              <strong>Edit playlist</strong> to pick from the catalog.
            </div>
          </div>
        ) : (
          <div className="wpl-video-grid">
            {selectedPlaylist.items.map((it) => (
              <WebVideoCard
                key={it.id}
                video={toWebVideo(it.video)}
                onPlay={onPlay}
              />
            ))}
          </div>
        )}

        {openPlaylist && (
          <PlaylistEditorModal
            key={openPlaylist.id}
            playlist={openPlaylist}
            token={accessToken ?? ""}
            onClose={() => setOpenEditorId(null)}
            onApply={handleApply}
            onDelete={() => handleDelete(openPlaylist.id)}
          />
        )}
        <WPLStyles />
      </div>
    );
  }

  return (
    <div className="fadeup w-resppad" style={{ padding: "24px 32px 64px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 22,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "var(--display)",
              fontSize: 36,
              fontWeight: 900,
              letterSpacing: "-0.02em",
              color: "var(--ink)",
              margin: 0,
            }}
          >
            Playlists
          </h1>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink-3)",
              marginTop: 6,
            }}
          >
            Curated queues your child can browse on the TV.
          </div>
        </div>
        {/* Header CTA is suppressed during the empty state — `EmptyState`
            renders its own "Create your first playlist" button below, and
            two side-by-side primary buttons read as a layout bug. Once at
            least one playlist exists, the header button is the only entry
            point for adding more. */}
        {!loading && !loadError && playlists.length > 0 && (
          <button
            className="btn"
            onClick={handleNew}
            disabled={creating}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Icon name="plus" size={14} />{" "}
            {creating ? "Creating…" : "New playlist"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="wpl-empty">Loading…</div>
      ) : loadError ? (
        <div className="wpl-empty wpl-error">{loadError}</div>
      ) : playlists.length === 0 ? (
        <EmptyState onCreate={handleNew} creating={creating} />
      ) : (
        <div className="wpl-grid">
          {playlists.map((p) => {
            const color = p.color ?? pickColor(p.id);
            const minutes = totalMinutes(p.items);
            return (
              <button
                key={p.id}
                className="wpl-card"
                onClick={() => setSelectedId(p.id)}
              >
                <div className="wpl-card-art" style={{ background: color }}>
                  <WebMascot mood="happy" color="#fff" size={120} />
                </div>
                <div className="wpl-card-meta">
                  <div className="wpl-card-eyebrow">PLAYLIST</div>
                  <div className="wpl-card-title">{p.name}</div>
                  <div className="wpl-card-sub">
                    {p.items.length}{" "}
                    {p.items.length === 1 ? "video" : "videos"} ·{" "}
                    {minutes} min
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {openPlaylist && (
        <PlaylistEditorModal
          key={openPlaylist.id}
          playlist={openPlaylist}
          token={accessToken ?? ""}
          onClose={() => setOpenEditorId(null)}
          onApply={handleApply}
          onDelete={() => handleDelete(openPlaylist.id)}
        />
      )}

      <WPLStyles />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Empty state — first-run prompt. Single CTA that triggers the same
// create flow as the header button.
// ─────────────────────────────────────────────────────────────────────────
function EmptyState({
  onCreate,
  creating,
}: {
  onCreate: () => void;
  creating: boolean;
}) {
  return (
    <div className="wpl-empty-card">
      <WebMascot mood="happy" color="#FF8A4C" size={120} />
      <div className="wpl-empty-title">No playlists yet</div>
      <div className="wpl-empty-sub">
        Group videos into themed queues — like &ldquo;Wind-down&rdquo; or
        &ldquo;Letters&rdquo; — so kids can launch a session in one tap on
        the TV.
      </div>
      <button
        className="btn"
        onClick={onCreate}
        disabled={creating}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          marginTop: 16,
        }}
      >
        <Icon name="plus" size={14} />{" "}
        {creating ? "Creating…" : "Create your first playlist"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PlaylistEditorModal — rename + recolor + reorder + add/remove + delete.
//
// The add-video flow opens a `VideoPickerPanel` inline (right side of the
// modal). The picker fetches the catalog + the parent's own generations
// once, then filters client-side as the parent types. Videos already in
// the playlist are shown as "added" and the button is disabled.
// ─────────────────────────────────────────────────────────────────────────
function PlaylistEditorModal({
  playlist,
  token,
  onClose,
  onApply,
  onDelete,
}: {
  playlist: Playlist;
  token: string;
  onClose: () => void;
  onApply: (p: Playlist) => void;
  onDelete: () => void;
}) {
  // Local working copies so the parent can rename without saving until
  // the field blurs / Save is clicked. Initial values reset whenever the
  // upstream playlist changes (e.g. another tab edits it).
  const [name, setName] = useState(playlist.name);
  const [color, setColor] = useState<string | null>(playlist.color);
  const [savingMeta, setSavingMeta] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    setName(playlist.name);
    setColor(playlist.color);
  }, [playlist.id, playlist.name, playlist.color]);

  const saveMeta = useCallback(async () => {
    if (!token) return;
    const trimmed = name.trim();
    if (!trimmed) {
      webAlert("Name can't be empty.");
      return;
    }
    if (trimmed === playlist.name && color === playlist.color) return;
    setSavingMeta(true);
    try {
      const updated = await playlistsApi.update(
        playlist.id,
        { name: trimmed, color },
        token,
      );
      onApply(updated);
    } catch (e) {
      webAlert(e instanceof Error ? e.message : "Couldn't save.");
    } finally {
      setSavingMeta(false);
    }
  }, [name, color, playlist.id, playlist.name, playlist.color, token, onApply]);

  const handleRemoveItem = useCallback(
    async (videoId: string) => {
      if (!token) return;
      try {
        const updated = await playlistsApi.removeItem(
          playlist.id,
          videoId,
          token,
        );
        onApply(updated);
      } catch (e) {
        webAlert(e instanceof Error ? e.message : "Couldn't remove.");
      }
    },
    [playlist.id, token, onApply],
  );

  const handleMove = useCallback(
    async (videoId: string, direction: -1 | 1) => {
      if (!token) return;
      const order = playlist.items.map((it) => it.videoId);
      const idx = order.indexOf(videoId);
      if (idx === -1) return;
      const next = idx + direction;
      if (next < 0 || next >= order.length) return;
      // Swap and ship — the backend persists the full new order.
      [order[idx], order[next]] = [order[next], order[idx]];
      try {
        const updated = await playlistsApi.reorder(playlist.id, order, token);
        onApply(updated);
      } catch (e) {
        webAlert(e instanceof Error ? e.message : "Couldn't reorder.");
      }
    },
    [playlist.id, playlist.items, token, onApply],
  );

  const handleAddVideos = useCallback(
    async (videoIds: string[]) => {
      if (!token || videoIds.length === 0) return;
      try {
        const updated = await playlistsApi.addItems(
          playlist.id,
          videoIds,
          token,
        );
        onApply(updated);
        setPickerOpen(false);
      } catch (e) {
        webAlert(e instanceof Error ? e.message : "Couldn't add videos.");
      }
    },
    [playlist.id, token, onApply],
  );

  const handleDelete = useCallback(async () => {
    if (!token) return;
    const ok = await webConfirm(
      `Delete "${playlist.name}"? Kids on the TV will lose access to this queue.`,
      { confirmLabel: "Delete", destructive: true },
    );
    if (!ok) return;
    try {
      await playlistsApi.remove(playlist.id, token);
      onDelete();
    } catch (e) {
      webAlert(e instanceof Error ? e.message : "Couldn't delete.");
    }
  }, [playlist.id, playlist.name, token, onDelete]);

  const effectiveColor = color ?? pickColor(playlist.id);
  const itemIds = useMemo(
    () => new Set(playlist.items.map((it) => it.videoId)),
    [playlist.items],
  );

  return (
    <div className="ws-modal" onClick={onClose}>
      <div
        className="ws-modal-card wpl-editor"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="ws-modal-close" onClick={onClose}>
          ×
        </button>

        <div className="wpl-editor-head" style={{ background: effectiveColor }}>
          <WebMascot mood="cheer" color="#fff" size={90} />
        </div>

        <div className="wpl-editor-body">
          <div className="wpl-editor-meta">
            <label className="wpl-field">
              <span className="wpl-field-label">Playlist name</span>
              <input
                className="w-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={saveMeta}
              />
            </label>
            <div className="wpl-field">
              <span className="wpl-field-label">Color</span>
              <div className="wpl-color-row">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    className={
                      "wpl-color-dot " +
                      (effectiveColor === c ? "selected" : "")
                    }
                    style={{ background: c }}
                    onClick={async () => {
                      setColor(c);
                      // Persist immediately on color pick — feels right
                      // because color is the card art, not a draft field.
                      if (!token) return;
                      try {
                        const updated = await playlistsApi.update(
                          playlist.id,
                          { color: c },
                          token,
                        );
                        onApply(updated);
                      } catch (e) {
                        webAlert(
                          e instanceof Error
                            ? e.message
                            : "Couldn't update color.",
                        );
                      }
                    }}
                    aria-label={`Set color ${c}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {savingMeta && (
            <div className="wpl-saving">Saving…</div>
          )}

          {/* Items list */}
          <div className="wpl-items-head">
            <div className="wpl-section-title">
              Videos in this playlist ({playlist.items.length})
            </div>
            <button
              className="btn"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              onClick={() => setPickerOpen((v) => !v)}
            >
              <Icon name="plus" size={12} />{" "}
              {pickerOpen ? "Close picker" : "Add videos"}
            </button>
          </div>

          {playlist.items.length === 0 ? (
            <div className="wpl-empty wpl-items-empty">
              No videos yet. Hit &ldquo;Add videos&rdquo; to fill the queue.
            </div>
          ) : (
            <div className="wpl-items">
              {playlist.items.map((it, idx) => (
                <div key={it.id} className="wpl-item">
                  <div className="wpl-item-pos">{idx + 1}</div>
                  <div className="wpl-item-art" style={{ background: effectiveColor }}>
                    <WebMascot mood="happy" color="#fff" size={28} />
                  </div>
                  <div className="wpl-item-info">
                    <div className="wpl-item-title">{it.video.title}</div>
                    <div className="wpl-item-sub">
                      {videoMinutes(it.video.duration)}{" "}
                      min
                      {it.video.parentId ? " · Yours" : ""}
                    </div>
                  </div>
                  <div className="wpl-item-actions">
                    <button
                      className="btn btn-ghost wpl-mini"
                      onClick={() => handleMove(it.videoId, -1)}
                      disabled={idx === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-ghost wpl-mini"
                      onClick={() => handleMove(it.videoId, 1)}
                      disabled={idx === playlist.items.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      className="btn btn-ghost wpl-mini wpl-danger"
                      onClick={() => handleRemoveItem(it.videoId)}
                      title="Remove from playlist"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pickerOpen && (
            <VideoPickerPanel
              token={token}
              excludeIds={itemIds}
              onConfirm={handleAddVideos}
              onClose={() => setPickerOpen(false)}
            />
          )}

          <div className="wpl-danger-row">
            <button className="btn btn-ghost wpl-danger" onClick={handleDelete}>
              Delete playlist
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// VideoPickerPanel — lists every video the parent can add to the playlist:
// seed catalog + the parent's own finished generations. Already-added
// videos are filtered out by the parent via `excludeIds`.
// ─────────────────────────────────────────────────────────────────────────
type PickerVideo = {
  id: string;
  title: string;
  duration: number;
  categories: string[];
  source: "catalog" | "mine";
};

function VideoPickerPanel({
  token,
  excludeIds,
  onConfirm,
  onClose,
}: {
  token: string;
  excludeIds: Set<string>;
  onConfirm: (videoIds: string[]) => void;
  onClose: () => void;
}) {
  const [videos, setVideos] = useState<PickerVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      contentApi.list({ take: 200 }, token),
      adminContentApi.listMyGenerations(token),
    ])
      .then(([catR, myR]) => {
        if (cancelled) return;
        const cat: PickerVideo[] =
          catR.status === "fulfilled"
            ? catR.value.videos.map(toPickerFromCatalog)
            : [];
        const mine: PickerVideo[] =
          myR.status === "fulfilled"
            ? myR.value
                .filter((v) => v.generationStatus === "done")
                .map(toPickerFromGenerated)
            : [];
        // Dedupe: the parent's own generations also appear in the catalog
        // (when childId is omitted). Prefer the "mine" source so the
        // picker shows them under the Yours bucket.
        const byId = new Map<string, PickerVideo>();
        for (const v of cat) byId.set(v.id, v);
        for (const v of mine) byId.set(v.id, v);
        setVideos(Array.from(byId.values()));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return videos
      .filter((v) => !excludeIds.has(v.id))
      .filter((v) => {
        if (!q) return true;
        return (
          v.title.toLowerCase().includes(q) ||
          v.categories.some((c) => c.toLowerCase().includes(q))
        );
      });
  }, [videos, query, excludeIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirm = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      onClose();
      return;
    }
    onConfirm(ids);
  };

  return (
    <div className="wpl-picker">
      <input
        className="w-input"
        placeholder="Search videos…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {loading ? (
        <div className="wpl-empty">Loading videos…</div>
      ) : filtered.length === 0 ? (
        <div className="wpl-empty">
          {videos.length === 0
            ? "No videos available."
            : "No matches — try a different search."}
        </div>
      ) : (
        <div className="wpl-picker-list">
          {filtered.map((v) => {
            const checked = selected.has(v.id);
            return (
              <label
                key={v.id}
                className={"wpl-picker-row " + (checked ? "checked" : "")}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(v.id)}
                />
                <div className="wpl-picker-info">
                  <div className="wpl-picker-title">{v.title}</div>
                  <div className="wpl-picker-sub">
                    {videoMinutes(v.duration)} min ·{" "}
                    {v.source === "mine" ? "Yours" : "Catalog"}
                    {v.categories[0] ? ` · ${v.categories[0]}` : ""}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}
      <div className="wpl-picker-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn" onClick={confirm} disabled={selected.size === 0}>
          Add {selected.size > 0 ? `(${selected.size})` : ""}
        </button>
      </div>
    </div>
  );
}

function toPickerFromCatalog(v: VideoListItem): PickerVideo {
  return {
    id: v.id,
    title: v.title,
    duration: v.duration,
    categories: v.categories ?? [],
    source: "catalog",
  };
}

function toPickerFromGenerated(v: GeneratedVideo): PickerVideo {
  return {
    id: v.id,
    title: v.title,
    duration: v.duration,
    categories: v.categories ?? [],
    source: "mine",
  };
}

function WPLStyles() {
  return (
    <style>{`
      /* Base modal shell. These used to be provided by WebStudio's <style>
         block, but Studio is hidden from the nav (so it's never mounted) —
         the playlist editor then rendered unstyled inline. Self-host them
         here so the editor is a proper centered overlay on this screen. */
      .ws-modal {
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.45);
        display: grid; place-items: center;
        padding: 24px;
        z-index: 100;
      }
      .ws-modal-card {
        width: min(640px, 100%);
        background: var(--surface);
        border-radius: 22px;
        overflow: hidden;
        position: relative;
        box-shadow: 0 30px 60px rgba(0,0,0,0.35);
      }
      .ws-modal-close {
        position: absolute; top: 12px; right: 12px;
        width: 34px; height: 34px; border-radius: 50%;
        border: 0; background: rgba(0,0,0,0.55); color: #fff;
        font: inherit; font-weight: 800; font-size: 18px;
        cursor: pointer; z-index: 1;
        line-height: 1;
      }
      .wpl-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 20px;
      }
      .wpl-video-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 18px;
      }
      .wpl-card {
        background: var(--surface);
        border: 1px solid var(--line);
        border-radius: 18px;
        overflow: hidden;
        padding: 0;
        text-align: left;
        cursor: pointer;
        font: inherit;
        color: inherit;
        transition: transform 120ms ease, box-shadow 120ms ease;
      }
      .wpl-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 24px rgba(0,0,0,0.08);
      }
      .wpl-card-art {
        aspect-ratio: 16 / 9;
        display: grid;
        place-items: center;
      }
      .wpl-card-meta { padding: 14px 16px 16px; }
      .wpl-card-eyebrow {
        font-size: 11px; font-weight: 900;
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--primary-ink);
      }
      .wpl-card-title {
        font-family: var(--display);
        font-size: 18px; font-weight: 900;
        color: var(--ink); margin-top: 4px;
        letter-spacing: -0.01em;
      }
      .wpl-card-sub {
        font-size: 13px; font-weight: 700;
        color: var(--ink-3); margin-top: 4px;
      }

      .wpl-empty {
        padding: 24px;
        text-align: center;
        color: var(--ink-3);
        font-weight: 700;
        font-size: 13px;
      }
      .wpl-error { color: #C0392B; }
      .wpl-empty-card {
        background: var(--surface);
        border: 1px dashed var(--line-strong);
        border-radius: 18px;
        padding: 56px 24px;
        display: flex; flex-direction: column; align-items: center;
        text-align: center;
        gap: 6px;
      }
      .wpl-empty-title {
        font-family: var(--display);
        font-size: 22px; font-weight: 900;
        color: var(--ink); margin-top: 8px;
      }
      .wpl-empty-sub {
        font-size: 14px; font-weight: 600;
        color: var(--ink-2);
        max-width: 480px;
        line-height: 1.5;
      }

      /* Editor */
      .wpl-editor {
        width: min(720px, 100%);
        max-height: calc(100vh - 48px);
        overflow-y: auto;
        padding: 0;
      }
      .wpl-editor-head {
        aspect-ratio: 16 / 5;
        display: grid; place-items: center;
      }
      .wpl-editor-body {
        padding: 22px 24px 22px;
        display: flex; flex-direction: column; gap: 14px;
      }
      .wpl-editor-meta {
        display: grid; grid-template-columns: 1fr auto;
        gap: 16px; align-items: end;
      }
      .wpl-field { display: flex; flex-direction: column; gap: 4px; }
      .wpl-field-label {
        font-size: 11px; font-weight: 800;
        letter-spacing: 0.08em; text-transform: uppercase;
        color: var(--ink-3);
      }
      .wpl-color-row {
        display: flex; gap: 8px;
      }
      .wpl-color-dot {
        width: 28px; height: 28px; border-radius: 50%;
        border: 0; cursor: pointer;
        box-shadow: inset 0 0 0 0 transparent;
        transition: transform 120ms ease, box-shadow 120ms ease;
      }
      .wpl-color-dot:hover { transform: scale(1.08); }
      .wpl-color-dot.selected {
        box-shadow: 0 0 0 3px #fff, 0 0 0 5px var(--ink);
      }
      .wpl-saving {
        font-size: 12px; font-weight: 700;
        color: var(--ink-3);
      }
      .wpl-items-head {
        display: flex; align-items: center; justify-content: space-between;
        margin-top: 6px;
      }
      .wpl-section-title {
        font-family: var(--display);
        font-size: 16px; font-weight: 900;
        color: var(--ink);
      }
      .wpl-items-empty {
        border: 1.5px dashed var(--line);
        border-radius: 12px;
        padding: 18px;
      }
      .wpl-items {
        display: flex; flex-direction: column; gap: 8px;
      }
      .wpl-item {
        display: flex; align-items: center; gap: 10px;
        padding: 10px 12px;
        border: 1px solid var(--line);
        border-radius: 12px;
      }
      .wpl-item-pos {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 12px; font-weight: 800;
        color: var(--ink-3);
        width: 22px; text-align: center;
        flex: 0 0 auto;
      }
      .wpl-item-art {
        width: 48px; height: 32px; border-radius: 6px;
        display: grid; place-items: center;
        flex: 0 0 auto;
      }
      .wpl-item-info { flex: 1; min-width: 0; }
      .wpl-item-title {
        font-size: 14px; font-weight: 800; color: var(--ink);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .wpl-item-sub {
        font-size: 12px; font-weight: 700; color: var(--ink-3);
      }
      .wpl-item-actions { display: flex; gap: 6px; flex: 0 0 auto; }
      .wpl-mini {
        padding: 6px 10px;
        font-size: 12px;
      }
      .wpl-danger { color: #C44; }
      .wpl-danger-row {
        margin-top: 18px;
        padding-top: 14px;
        border-top: 1px solid var(--line);
        display: flex; justify-content: flex-end;
      }

      /* Picker */
      .wpl-picker {
        border: 1.5px solid var(--primary);
        border-radius: 14px;
        padding: 14px;
        background: var(--bg-warm);
        display: flex; flex-direction: column; gap: 10px;
      }
      .wpl-picker-list {
        max-height: 280px;
        overflow-y: auto;
        display: flex; flex-direction: column; gap: 6px;
      }
      .wpl-picker-row {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 10px;
        background: var(--surface);
        border: 1.5px solid var(--line);
        border-radius: 10px;
        cursor: pointer;
      }
      .wpl-picker-row:hover { border-color: var(--primary); }
      .wpl-picker-row.checked {
        border-color: var(--primary);
        background: #FFF1E2;
      }
      .wpl-picker-row input[type="checkbox"] {
        width: 16px; height: 16px;
        accent-color: #FF8A4C;
        flex: 0 0 auto;
      }
      .wpl-picker-info { flex: 1; min-width: 0; }
      .wpl-picker-title {
        font-size: 13px; font-weight: 800; color: var(--ink);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .wpl-picker-sub {
        font-size: 11px; font-weight: 700; color: var(--ink-3);
      }
      .wpl-picker-actions {
        display: flex; justify-content: flex-end; gap: 8px;
      }
    `}</style>
  );
}

export default WebPlaylists;
